// app/router.tsx
import { createRouter, Navigate, RouterProvider } from "@tanstack/react-router";
import { useAuth0 } from "@auth0/auth0-react";
import { routeTree } from "./routeTree.gen";
import { Spinner } from "./components/ui/spinner";
import type { AuthUser } from "./hooks/useAuth0Login";
import { useAuth0Login } from "./hooks/useAuth0Login";
import {
  AuthenticatedUserProvider,
  useAuthenticatedUser,
} from "./hooks/useAuthenticatedUser";

// Create router instance with proper context typing
export const router = createRouter({
  routeTree,
  scrollRestoration: true,
  defaultNotFoundComponent: AppNotFound,
  context: {
    auth: undefined as never,
  },
});

// Register router for type safety and define context type
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Define the router context type for beforeLoad functions
export interface RouterContext {
  auth: {
    isAuthenticated: boolean;
    isLoading: boolean;
    user?: AuthUser;
    loginWithRedirect: () => void;
    logout: () => void;
  };
}

function AppNotFound() {
  const { isAuthenticated } = useAuth0Login();
  const { user, routing, isLoading: isUserLoading } = useAuthenticatedUser();

  if (isAuthenticated && isUserLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Spinner className="size-8 text-brand-green-primary" />
      </div>
    );
  }

  if (isAuthenticated && user) {
    return <Navigate to={(routing?.homePath ?? "/dashboard")} replace />;
  }

  return (
    <div className="min-h-screen bg-white flex flex-col justify-center items-center gap-4 p-8">
      <h1 className="text-2xl font-bold text-black">Page Not Found</h1>
      <p className="text-black text-center max-w-md">
        The page you are looking for does not exist.
      </p>
    </div>
  );
}

export function Router() {
  const auth = useAuth0<AuthUser>();

  return (
    <AuthenticatedUserProvider>
      <RouterProvider router={router} context={{ auth }} />
    </AuthenticatedUserProvider>
  );
}
